import { get } from 'svelte/store';
import { profile_data } from 'src/stores/account/user';
import { save_profile } from 'src/helpers/profile_helpers';
import { scramblePacket, unscramblePacket } from 'src/helpers/profile_packet_scrambler';
import {
	BANNER_VIEWS_CTA_MAX,
	BANNER_VIEWS_PROFILE_KEY,
	BANNER_VIEWS_SCHEMA_VERSION,
	type BannerDismissSource,
	type BannerId,
	type BannerRecord,
	type BannerScope,
	type BannerSurfaceState,
	type BannerViewsDocument,
	type BannerViewsWriteResult,
	type SurfaceId,
} from 'src/types/banner-views';
import {
	emptyBannerViewsDocument,
	mergeBannerViewsDocuments,
	sanitizeBannerViewsDocument,
	validateBannerViewsDocument,
} from './banner-views-validate';

export type BannerMutationOptions = {
	surface?: SurfaceId;
	extras?: Record<string, unknown>;
	note?: string;
	now?: number;
};

export type DismissBannerOptions = BannerMutationOptions & {
	snoozeMs?: number;
	dismissSource?: BannerDismissSource;
};

export type TrackCtaOptions = BannerMutationOptions & {
	href?: string;
};

const nowMs = (now?: number) => (typeof now === 'number' && Number.isFinite(now) ? now : Date.now());

const ensureRecord = (doc: BannerViewsDocument, id: BannerId, scope: BannerScope): BannerRecord => {
	const existing = doc.banners[id];
	if (existing) return existing;
	return {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		id,
		scope,
	};
};

const appendCta = (
	ctas: BannerRecord['ctas'] | undefined,
	event: { id: string; at: number; surface?: string; href?: string },
) => [...(ctas || []), event].slice(-BANNER_VIEWS_CTA_MAX);

const isHiddenByDismiss = (
	dismissedAt: number | undefined,
	snoozeUntil: number | undefined,
	now: number,
): boolean => {
	if (dismissedAt === undefined) return false;
	if (snoozeUntil === undefined) return true; // permanent
	return now < snoozeUntil;
};

/**
 * Read profile banner_views (unscramble envelope when present, then sanitize).
 * In-memory callers always receive a clear BannerViewsDocument.
 */
export const getBannerViews = (): BannerViewsDocument => {
	const raw = get(profile_data)?.[BANNER_VIEWS_PROFILE_KEY];
	if (!raw) return emptyBannerViewsDocument();
	const plain = unscramblePacket(raw);
	if (plain === null) return emptyBannerViewsDocument();
	const { document } = sanitizeBannerViewsDocument(plain);
	return document ?? emptyBannerViewsDocument();
};

export const getBanner = (id: BannerId): BannerRecord | undefined => getBannerViews().banners[id];

/**
 * Whether the banner should show.
 * - shared: uses banner-level dismiss / snooze / convertedAt
 * - per-surface: uses that surface's flags (surface required)
 */
export const isBannerVisible = (
	id: BannerId,
	surface?: SurfaceId,
	now: number = Date.now(),
): boolean => {
	const record = getBanner(id);
	if (!record) return true;

	// Funnel complete — hide on all surfaces.
	if (record.convertedAt !== undefined) return false;

	if (record.scope === 'shared') {
		return !isHiddenByDismiss(record.dismissedAt, record.snoozeUntil, now);
	}

	if (!surface) return true;
	const surfaceState = record.surfaces?.[surface];
	if (!surfaceState) return true;
	return !isHiddenByDismiss(surfaceState.dismissedAt, surfaceState.snoozeUntil, now);
};

/**
 * Absolute reshow time, or undefined if visible now / permanently hidden.
 * - shared: banner snoozeUntil
 * - per-surface + surface: that surface's snoozeUntil
 * - per-surface without surface: earliest pending snoozeAmong surfaces still hidden
 */
export const getBannerReshowAt = (
	id: BannerId,
	surface?: SurfaceId,
	now: number = Date.now(),
): number | undefined => {
	const record = getBanner(id);
	if (!record) return undefined;

	if (record.scope === 'shared') {
		if (record.dismissedAt === undefined) return undefined;
		if (record.snoozeUntil === undefined) return undefined; // permanent
		return record.snoozeUntil > now ? record.snoozeUntil : undefined;
	}

	if (surface) {
		const s = record.surfaces?.[surface];
		if (!s?.dismissedAt) return undefined;
		if (s.snoozeUntil === undefined) return undefined;
		return s.snoozeUntil > now ? s.snoozeUntil : undefined;
	}

	const pending: number[] = [];
	for (const s of Object.values(record.surfaces || {})) {
		if (!s.dismissedAt) continue;
		if (s.snoozeUntil === undefined) continue; // permanent on that surface
		if (s.snoozeUntil > now) pending.push(s.snoozeUntil);
	}
	if (pending.length === 0) return undefined;
	return Math.min(...pending);
};

const persistDocument = async (document: BannerViewsDocument): Promise<BannerViewsWriteResult> => {
	const validated = validateBannerViewsDocument(document);
	if (validated.ok === false) {
		console.error('banner_views validation failed; not saving', validated.errors);
		return { ok: false, errors: validated.errors };
	}

	const nextDocument = validated.document;
	const packet = scramblePacket(nextDocument);

	profile_data.update((current) => ({
		...current,
		[BANNER_VIEWS_PROFILE_KEY]: packet,
	}));

	const res = await save_profile(BANNER_VIEWS_PROFILE_KEY, packet);
	if (!res?.isok) {
		return { ok: false, errors: ['save_profile failed'] };
	}
	return { ok: true, document: nextDocument };
};

const applyBannerPatch = async (
	id: BannerId,
	scope: BannerScope,
	mutate: (record: BannerRecord) => BannerRecord,
): Promise<BannerViewsWriteResult> => {
	const current = getBannerViews();
	const base = ensureRecord(current, id, scope);
	const nextRecord = mutate({ ...base, surfaces: { ...(base.surfaces || {}) } });

	const patch: BannerViewsDocument = {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners: { [id]: nextRecord },
	};
	const merged = mergeBannerViewsDocuments(current, patch);
	return persistDocument(merged);
};

export const markBannerSeen = async (
	id: BannerId,
	options: BannerMutationOptions & { scope?: BannerScope } = {},
): Promise<BannerViewsWriteResult> => {
	const now = nowMs(options.now);
	const existing = getBanner(id);
	const scope = options.scope ?? existing?.scope ?? (options.surface ? 'per-surface' : 'shared');

	return applyBannerPatch(id, scope, (record) => {
		record.firstSeenAt = record.firstSeenAt ?? now;
		record.lastSeenAt = now;
		record.impressionCount = (record.impressionCount ?? 0) + 1;
		if (options.extras) {
			record.extras = { ...(record.extras || {}), ...options.extras };
		}
		if (options.note !== undefined) record.note = options.note;

		if (options.surface) {
			const prev: BannerSurfaceState = { ...(record.surfaces?.[options.surface] || {}) };
			prev.firstSeenAt = prev.firstSeenAt ?? now;
			prev.lastSeenAt = now;
			prev.impressionCount = (prev.impressionCount ?? 0) + 1;
			if (options.extras) {
				prev.extras = { ...(prev.extras || {}), ...options.extras };
			}
			record.surfaces = { ...(record.surfaces || {}), [options.surface]: prev };
		}
		return record;
	});
};

export const dismissBanner = async (
	id: BannerId,
	options: DismissBannerOptions & { scope?: BannerScope } = {},
): Promise<BannerViewsWriteResult> => {
	const now = nowMs(options.now);
	const existing = getBanner(id);
	const scope = options.scope ?? existing?.scope ?? (options.surface ? 'per-surface' : 'shared');
	const dismissSource = options.dismissSource ?? 'user';
	const snoozeUntil =
		typeof options.snoozeMs === 'number' && options.snoozeMs > 0
			? now + options.snoozeMs
			: undefined;

	return applyBannerPatch(id, scope, (record) => {
		if (scope === 'shared' || !options.surface) {
			record.dismissedAt = record.dismissedAt ?? now;
			record.lastDismissedAt = now;
			record.dismissCount = (record.dismissCount ?? 0) + 1;
			record.dismissSource = dismissSource;
			if (snoozeUntil !== undefined) {
				record.snoozeUntil = snoozeUntil;
			} else {
				delete record.snoozeUntil;
			}
			if (options.note !== undefined) record.note = options.note;
		}

		if (options.surface) {
			const prev: BannerSurfaceState = { ...(record.surfaces?.[options.surface] || {}) };
			prev.dismissedAt = prev.dismissedAt ?? now;
			prev.lastDismissedAt = now;
			prev.dismissCount = (prev.dismissCount ?? 0) + 1;
			prev.dismissSource = dismissSource;
			if (snoozeUntil !== undefined) {
				prev.snoozeUntil = snoozeUntil;
			} else {
				delete prev.snoozeUntil;
			}
			if (options.note !== undefined) prev.note = options.note;
			record.surfaces = { ...(record.surfaces || {}), [options.surface]: prev };
		}

		return record;
	});
};

export const trackBannerCta = async (
	id: BannerId,
	ctaId: string,
	options: TrackCtaOptions & { scope?: BannerScope } = {},
): Promise<BannerViewsWriteResult> => {
	const now = nowMs(options.now);
	const existing = getBanner(id);
	const scope = options.scope ?? existing?.scope ?? (options.surface ? 'per-surface' : 'shared');

	return applyBannerPatch(id, scope, (record) => {
		const event = {
			id: ctaId,
			at: now,
			...(options.surface ? { surface: options.surface } : {}),
			...(options.href ? { href: options.href } : {}),
		};
		record.ctas = appendCta(record.ctas, event);
		record.lastCtaId = ctaId;
		record.lastCtaAt = now;

		if (options.surface) {
			const prev: BannerSurfaceState = { ...(record.surfaces?.[options.surface] || {}) };
			prev.ctas = appendCta(prev.ctas, event);
			prev.lastCtaId = ctaId;
			prev.lastCtaAt = now;
			record.surfaces = { ...(record.surfaces || {}), [options.surface]: prev };
		}
		return record;
	});
};

export const markBannerConverted = async (
	id: BannerId,
	options: BannerMutationOptions & { scope?: BannerScope } = {},
): Promise<BannerViewsWriteResult> => {
	const now = nowMs(options.now);
	const existing = getBanner(id);
	const scope = options.scope ?? existing?.scope ?? 'shared';

	return applyBannerPatch(id, scope, (record) => {
		record.convertedAt = record.convertedAt ?? now;
		if (options.extras) {
			record.extras = { ...(record.extras || {}), ...options.extras };
		}
		return record;
	});
};

export const setBannerNote = async (
	id: BannerId,
	note: string,
	options: { surface?: SurfaceId; scope?: BannerScope } = {},
): Promise<BannerViewsWriteResult> => {
	const existing = getBanner(id);
	const scope = options.scope ?? existing?.scope ?? (options.surface ? 'per-surface' : 'shared');

	return applyBannerPatch(id, scope, (record) => {
		if (options.surface) {
			const prev: BannerSurfaceState = { ...(record.surfaces?.[options.surface] || {}) };
			prev.note = note;
			record.surfaces = { ...(record.surfaces || {}), [options.surface]: prev };
		} else {
			record.note = note;
		}
		return record;
	});
};

export const clearBanner = async (id: BannerId): Promise<BannerViewsWriteResult> => {
	const current = getBannerViews();
	if (!current.banners[id]) {
		return { ok: true, document: current };
	}
	const { [id]: _removed, ...rest } = current.banners;
	return persistDocument({
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners: rest,
	});
};

export const clearBannerSurface = async (
	id: BannerId,
	surface: SurfaceId,
): Promise<BannerViewsWriteResult> => {
	const current = getBannerViews();
	const record = current.banners[id];
	if (!record?.surfaces?.[surface]) {
		return { ok: true, document: current };
	}
	const { [surface]: _removed, ...restSurfaces } = record.surfaces;
	const nextRecord: BannerRecord = {
		...record,
		surfaces: Object.keys(restSurfaces).length ? restSurfaces : undefined,
	};
	return persistDocument({
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners: {
			...current.banners,
			[id]: nextRecord,
		},
	});
};

/** Replace entire document after validation (used by migrations). */
export const replaceBannerViewsDocument = async (
	document: BannerViewsDocument,
): Promise<BannerViewsWriteResult> => persistDocument(document);
